import React from 'react';
import { useTranslation } from 'react-i18next';
import type { FormInstance } from 'antd';
import { Button, Checkbox, Drawer, Form, Select, Space } from 'antd';
import debounce from 'lodash.debounce';
import { filterRangePresets } from 'shared/constants/filters.ts';
import type { IOrdersFilter } from 'shared/stores/use-order-filters.ts';
import { useOrderFiltersStore } from 'shared/stores/use-order-filters.ts';
import { transformAndSetFilters } from 'shared/utils/filters/transform-and-set-filters.ts';
import { OrderStatus } from 'types/order.ts';
import { ClientFormSelect } from 'components/common/client-form-select';
import { DataRangeFormPicker } from 'components/common/data-range-form-picker';
import { UserFormSelect } from 'components/common/user-form-select';
import styles from './orders-filters-drawer.module.scss';

interface IOrdersFiltersProps {
  form: FormInstance;
  isOpen: boolean;
  onClose: () => void;
}

export const OrdersFiltersDrawer: React.FC<IOrdersFiltersProps> = (props) => {
  const { form, isOpen, onClose } = props;
  const { t } = useTranslation();
  const { filters, search, setFilters } = useOrderFiltersStore();

  const handleSuccess = debounce(() => transformAndSetFilters<IOrdersFilter>(form, setFilters), 300);

  const handleClearFilters = () => {
    setFilters(undefined);
    setTimeout(() => form.resetFields(), 0);
    onClose();
  };

  const clearButton = filters ? (
    <Button type='link' size='small' onClick={handleClearFilters}>
      {t('common:clear_filters')}
    </Button>
  ) : null;

  return (
    <Drawer placement='right' open={isOpen} extra={[clearButton]} onClose={onClose}>
      <Form form={form} name='orders-filter' layout='horizontal' initialValues={filters} className={styles.form}>
        <Space direction='vertical' size='middle' className={styles.box}>
          <Form.Item name='statuses' className={styles.input}>
            <Select placeholder='Statuses' mode='multiple' disabled={!!search} allowClear onChange={handleSuccess}>
              <Select.Option value={OrderStatus.accepted}>{OrderStatus.accepted}</Select.Option>
              <Select.Option value={OrderStatus.progress}>{OrderStatus.progress}</Select.Option>
              <Select.Option value={OrderStatus.delivered}>{OrderStatus.delivered}</Select.Option>
              <Select.Option value={OrderStatus.cancelled}>{OrderStatus.cancelled}</Select.Option>
              <Select.Option value={OrderStatus.failed}>{OrderStatus.failed}</Select.Option>
            </Select>
          </Form.Item>
          <ClientFormSelect
            name='clientId'
            placeholder={t('client:client')}
            handleClientChange={handleSuccess}
            className={styles.input}
            allowClear
          />
          <UserFormSelect
            name='userId'
            placeholder={t('common:manager')}
            handleUserChange={handleSuccess}
            className={styles.input}
            disabled={!!search}
            allowClear
          />
          <DataRangeFormPicker
            name='createdAt'
            handleSuccess={handleSuccess}
            isRequired={false}
            className={styles.input}
            rangePresets={filterRangePresets}
          />
          <Form.Item name='withoutMainInvoice' className={styles.input} valuePropName='checked'>
            <Checkbox onChange={handleSuccess}>Without main invoice</Checkbox>
          </Form.Item>
        </Space>
      </Form>
    </Drawer>
  );
};
